import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Loader2, Trash2, ImagePlus, Film, X } from "lucide-react";
import AdCreative, { LAYOUTS } from "@/components/AdCreative";

const DEFAULT_ADJ = { fit: "contain", zoom: 100, pos_x: 50, pos_y: 50 };
const EMPTY_FORM = { title: "", advertiser: "", link_url: "", size: "banner", start_date: "", end_date: "", headline: "", subtext: "", cta_label: "", weight: 1 };
const WEIGHT_LABEL = (w) => (w >= 8 ? "Premium" : w >= 4 ? "Boosted" : "Standard");

export function WeightSlider({ value, onChange, share, testid = "ad-weight" }) {
  const w = Number(value) || 1;
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid={testid}>
      <label className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
        Rotation weight
        <input data-testid={`${testid}-slider`} type="range" min={1} max={10} step={1} value={w}
          onChange={(e) => onChange(Number(e.target.value))} className="w-36 accent-[#A3E635]" />
        <span className="font-mono text-white text-xs" data-testid={`${testid}-value`}>{w}×</span>
        <span className={`px-1.5 py-0.5 border text-[8px] ${w >= 8 ? "border-amber-400 text-amber-300" : w >= 4 ? "border-pb-cyan text-pb-cyan" : "border-zinc-700 text-zinc-500"}`} data-testid={`${testid}-tier`}>{WEIGHT_LABEL(w)}</span>
      </label>
      {share && Object.keys(share).length > 0 && (
        <div className="flex flex-wrap gap-1 text-[8px] uppercase tracking-wider text-zinc-500" data-testid={`${testid}-share`}>
          {Object.entries(share).map(([z, pct]) => <span key={z} className="border border-pb-border px-1.5 py-0.5">{z} <span className="text-lime-300 font-mono">{pct}%</span></span>)}
        </div>
      )}
    </div>
  );
}

export function AdArtworkAdjuster({ src, size, value, onChange, ad = {}, testid = "ad-adjuster" }) {
  const boxRef = useRef(null);
  const drag = useRef(null);
  const v = { ...DEFAULT_ADJ, ...value };
  const set = (patch) => onChange({ ...v, ...patch });

  const onDown = (e) => {
    drag.current = { x: e.clientX, y: e.clientY, px: v.pos_x, py: v.pos_y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const nx = drag.current.px - ((e.clientX - drag.current.x) / r.width) * 100;
    const ny = drag.current.py - ((e.clientY - drag.current.y) / r.height) * 100;
    set({ pos_x: Math.max(0, Math.min(100, nx)), pos_y: Math.max(0, Math.min(100, ny)) });
  };
  const onUp = () => { drag.current = null; };

  return (
    <div className="w-full space-y-2" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
        Live preview — exactly how the {LAYOUTS[size]?.label || size} runs on the site · whole {ad.video ? "video" : "image"} fits by default · drag to nudge
      </div>
      <div className="relative w-full border border-pb-cyan/50 overflow-hidden" data-testid={`${testid}-preview`}>
        <AdCreative ad={{ ...ad, ...v, size, image: src }}
          imageProps={{ ref: boxRef, onPointerDown: onDown, onPointerMove: onMove, onPointerUp: onUp, onPointerLeave: onUp, className: "cursor-move select-none touch-none" }} />
        <span className="absolute top-1 right-1 bg-black/70 text-zinc-400 text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 pointer-events-none">Sponsored</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {[["contain", "Fit whole image"], ["cover", "Fill the cell"], ["custom", "Custom zoom"]].map(([f, label]) => (
          <button key={f} data-testid={`${testid}-fit-${f}`} onClick={() => set({ fit: f })}
            className={`px-2.5 py-1.5 border text-[9px] font-bold uppercase tracking-wide transition-colors ${
              v.fit === f ? "border-pb-cyan bg-pb-cyan/10 text-pb-cyan" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
            {label}
          </button>
        ))}
        {v.fit === "custom" && (
          <label className="flex items-center gap-2 text-[9px] uppercase tracking-widest text-zinc-500 font-bold">
            Zoom
            <input data-testid={`${testid}-zoom`} type="range" min={25} max={400} step={5} value={v.zoom}
              onChange={(e) => set({ zoom: Number(e.target.value) })} className="w-32 accent-[#22D3EE]" />
            <span className="font-mono text-zinc-300">{v.zoom}%</span>
          </label>
        )}
        <button data-testid={`${testid}-reset`} onClick={() => onChange({ ...DEFAULT_ADJ })}
          className="px-2.5 py-1.5 border border-pb-border text-[9px] font-bold uppercase tracking-wide text-zinc-500 hover:text-white transition-colors">
          Reset
        </button>
      </div>
    </div>
  );
}

export default function AdManagerPanel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [zones, setZones] = useState([]);
  const [image, setImage] = useState(null);
  const [image2, setImage2] = useState(null);
  const [video, setVideo] = useState(null);
  const [vidProgress, setVidProgress] = useState(null);
  const file2Ref = useRef(null);
  const videoRef = useRef(null);
  const [adj, setAdj] = useState({ ...DEFAULT_ADJ });
  const [editing, setEditing] = useState(null);
  const [editAdj, setEditAdj] = useState(null);
  const [busy, setBusy] = useState(null);
  const fileRef = useRef(null);

  const load = () => { api.get("/admin/ads").then(({ data: d }) => setData(d)).catch(() => {}); };
  useEffect(load, []);

  if (!data) return null;

  const readFile = (e, set) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => set(r.result);
    r.readAsDataURL(f);
  };
  const pickImage = (e) => readFile(e, setImage);
  const pickImage2 = (e) => readFile(e, setImage2);
  const pickVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) return toast.error("Video is too large — keep ad clips under 200MB");
    setVidProgress(0);
    try {
      const { data: sig } = await api.get("/cloudinary/signature?folder=uploads/ads");
      const fd = new FormData();
      fd.append("file", file); fd.append("api_key", sig.api_key); fd.append("timestamp", sig.timestamp);
      fd.append("signature", sig.signature); fd.append("folder", sig.folder);
      const res = await axios.post(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/video/upload`, fd, {
        onUploadProgress: (ev) => setVidProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0),
      });
      const { public_id, format, duration } = res.data;
      if (duration && duration > 61) { toast.error(`Clip is ${Math.round(duration)}s — ad videos max out at 60 seconds`); return; }
      setVideo(format === "mp4" ? res.data.secure_url : `https://res.cloudinary.com/${sig.cloud_name}/video/upload/q_auto/${public_id}.mp4`);
      toast.success("Video uploaded — it autoplays muted & loops in the ad slot");
    } catch (err) {
      const cldErr = err.response?.data?.error?.message;
      toast.error(cldErr ? `Video upload failed — ${cldErr}` : formatApiError(err.response?.data?.detail));
    } finally { setVidProgress(null); }
  };
  const layout = LAYOUTS[form.size] || LAYOUTS.banner;
  const needsCopy = layout.kind === "text";
  const needsTwo = layout.kind === "twoup";
  const canLaunch = form.title.trim() && zones.length > 0 && (image || video) && (!needsCopy || form.headline.trim()) && (!needsTwo || image2);

  const create = async () => {
    setBusy("add");
    try {
      await api.post("/admin/ads", { ...form, start_date: form.start_date || null, end_date: form.end_date || null, zones, image, image2: needsTwo ? image2 : null, video, active: true, ...adj });
      toast.success(`Ad "${form.title}" is live in ${zones.length} zone${zones.length === 1 ? "" : "s"}`);
      setForm({ ...EMPTY_FORM });
      setZones([]); setImage(null); setImage2(null); setVideo(null); setAdj({ ...DEFAULT_ADJ });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const toggle = async (a) => {
    try {
      await api.put(`/admin/ads/${a.id}`, { ...a, active: !a.active, image: null, image2: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (a) => {
    try { await api.delete(`/admin/ads/${a.id}`); toast.success(`"${a.title}" deleted`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveWeight = async (a, weight) => {
    try {
      await api.put(`/admin/ads/${a.id}`, { ...a, image: null, image2: null, weight });
      setData((d) => ({ ...d, ads: d.ads.map((x) => (x.id === a.id ? { ...x, weight } : x)) }));
      toast.success(`"${a.title}" now rotates at ${weight}× (${WEIGHT_LABEL(weight)})`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveAdjust = async (a) => {
    setBusy(`adj-${a.id}`);
    try {
      await api.put(`/admin/ads/${a.id}`, { ...a, image: null, image2: null, ...editAdj });
      toast.success("Artwork position saved — live everywhere instantly");
      setEditing(null); setEditAdj(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="ad-manager-panel">
      <p className="text-xs text-zinc-500">
        Sell ad space across the whole app. Pick where it shows (any mix of 8 zones), the layout (8 styles — plain
        image cells, square tile, poster, headline card, overlay, two-up), and how long (start–end dates, blank = forever).
        Uploaded artwork or <span className="text-zinc-400">video</span> (autoplays muted & loops, up to 60s) always scales to fit inside the layout — you see the whole thing, never cropped — unless you
        choose Fill or Custom zoom. Impressions and clicks are tracked per ad. <span className="text-zinc-400">Rotation weight</span> (1×–10×)
        decides how often an ad wins the slot when several share a zone — a 5× premium ad shows five times as often as a 1× ad.
      </p>

      <div className="border border-pb-border bg-pb-elevated/40 p-4 space-y-2.5">
        <div className="flex flex-wrap gap-2.5">
          <input data-testid="ad-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Ad title *" className="flex-1 min-w-[150px] bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <input data-testid="ad-advertiser" value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })}
            placeholder="Advertiser / merchant" className="flex-1 min-w-[150px] bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
        </div>
        <input data-testid="ad-link" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })}
          placeholder="Click-through URL (https://…)" className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Placement zones</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.zones).map(([z, label]) => (
              <button key={z} data-testid={`ad-zone-${z}`}
                onClick={() => setZones(zones.includes(z) ? zones.filter((x) => x !== z) : [...zones, z])}
                className={`px-2.5 py-1.5 border text-[9px] font-bold uppercase tracking-wide transition-colors ${
                  zones.includes(z) ? "border-pb-red bg-pb-red/10 text-pb-red" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}
                title={label}>
                {z}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Layout</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5" data-testid="ad-layout-picker">
            {Object.entries(LAYOUTS).map(([s, l]) => (
              <button key={s} data-testid={`ad-size-${s}`} onClick={() => setForm({ ...form, size: s })}
                className={`px-2.5 py-2 border text-left transition-colors ${
                  form.size === s ? "border-pb-cyan bg-pb-cyan/10 text-pb-cyan" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
                <div className="text-[10px] font-black uppercase tracking-wide">{l.label}</div>
                <div className="text-[8px] text-zinc-500 leading-tight">{l.hint}</div>
              </button>
            ))}
          </div>
        </div>
        {needsCopy && (
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2.5" data-testid="ad-copy-fields">
            <input data-testid="ad-headline" value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} maxLength={60}
              placeholder="Headline * (white glow)" className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
            <input data-testid="ad-subtext" value={form.subtext} onChange={(e) => setForm({ ...form, subtext: e.target.value })} maxLength={120}
              placeholder="One-line subtext" className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
            <input data-testid="ad-cta" value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} maxLength={24}
              placeholder="Button text (Shop now)" className="bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red sm:w-44" />
          </div>
        )}
        <WeightSlider value={form.weight} onChange={(w) => setForm({ ...form, weight: w })} testid="ad-weight-new" />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Runs from</div>
            <input data-testid="ad-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none focus:border-pb-red" />
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-bold">Until</div>
            <input data-testid="ad-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none focus:border-pb-red" />
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="ad-image-input" />
          <button onClick={() => fileRef.current?.click()} data-testid="ad-image-btn"
            className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:border-zinc-400 transition-colors">
            <ImagePlus className="w-3.5 h-3.5" /> {image ? "Artwork added ✓" : video ? "Poster image (optional)" : "Upload artwork *"}
          </button>
          {image && <img src={image} alt="" className="h-10 border border-pb-border object-contain bg-black" />}
          <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={pickVideo} data-testid="ad-video-input" />
          <button onClick={() => videoRef.current?.click()} disabled={vidProgress !== null} data-testid="ad-video-btn"
            className={`flex items-center gap-1.5 border text-[10px] font-black uppercase tracking-widest px-3 py-2.5 transition-colors disabled:opacity-60 ${video ? "border-pb-cyan text-pb-cyan" : "border-pb-border text-zinc-400 hover:border-zinc-400"}`}>
            {vidProgress !== null ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Film className="w-3.5 h-3.5" />}
            {vidProgress !== null ? `Uploading ${vidProgress}%` : video ? "Video added ✓" : image ? "Or upload video" : "Upload video *"}
          </button>
          {video && (
            <button onClick={() => setVideo(null)} data-testid="ad-video-clear" title="Remove video"
              className="p-1.5 border border-pb-border text-zinc-500 hover:text-red-400 hover:border-red-400/60 transition-colors"><X className="w-3.5 h-3.5" /></button>
          )}
          {needsTwo && (
            <>
              <input ref={file2Ref} type="file" accept="image/*" className="hidden" onChange={pickImage2} data-testid="ad-image2-input" />
              <button onClick={() => file2Ref.current?.click()} data-testid="ad-image2-btn"
                className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2.5 hover:border-zinc-400 transition-colors">
                <ImagePlus className="w-3.5 h-3.5" /> {image2 ? "2nd image added ✓" : "Upload 2nd image *"}
              </button>
              {image2 && <img src={image2} alt="" className="h-10 border border-pb-border object-contain bg-black" />}
            </>
          )}
          <button data-testid="ad-create-btn" onClick={create}
            disabled={busy === "add" || !canLaunch}
            className="flex items-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-5 py-2.5 text-[10px] hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
            {busy === "add" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Launch Ad
          </button>
        </div>
        {vidProgress !== null && (
          <div className="h-1.5 w-full bg-pb-elevated overflow-hidden" data-testid="ad-video-progress"><div className="h-full bg-pb-cyan transition-all" style={{ width: `${vidProgress}%` }} /></div>
        )}
        {(image || video) && <AdArtworkAdjuster src={image} size={form.size} value={adj} onChange={setAdj} testid="ad-adjuster-new"
          ad={{ title: form.title, advertiser: form.advertiser, headline: form.headline, subtext: form.subtext, cta_label: form.cta_label, image2, video }} />}
      </div>

      <div className="divide-y divide-pb-border/40 border border-pb-border" data-testid="ad-list">
        {data.ads.length === 0 && <div className="p-4 text-xs text-zinc-600">No ads yet — launch your first banner above.</div>}
        {data.ads.map((a) => (
          <div key={a.id} className="px-3 py-2.5" data-testid={`ad-row-${a.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            {a.video ? (
              <div className="relative h-10 w-24 bg-black border border-pb-border overflow-hidden" data-testid={`ad-thumb-video-${a.id}`}>
                <video src={a.video} muted playsInline preload="metadata" className="h-full w-full object-contain" />
                <span className="absolute bottom-0.5 right-0.5 bg-pb-cyan text-black text-[7px] font-black uppercase px-1 leading-tight">Video</span>
              </div>
            ) : <img src={a.image} alt="" className="h-10 w-24 object-contain bg-black border border-pb-border" />}
            <div className="flex-1 min-w-[140px]">
              <div className="text-xs font-semibold">{a.title} {a.advertiser && <span className="text-zinc-500">— {a.advertiser}</span>}</div>
              <div className="text-[9px] text-zinc-600">
                {a.zones.join(", ")} · {LAYOUTS[a.size]?.label || a.size} · {a.start_date || "now"} → {a.end_date || "forever"}
              </div>
              <div className="mt-1.5">
                <WeightSlider value={a.weight} share={a.share} testid={`ad-weight-${a.id}`}
                  onChange={(w) => setData((d) => ({ ...d, ads: d.ads.map((x) => (x.id === a.id ? { ...x, weight: w, _dirty: true } : x)) }))} />
                {a._dirty && (
                  <button data-testid={`ad-weight-save-${a.id}`} onClick={() => saveWeight(a, a.weight)}
                    className="mt-1.5 bg-pb-red text-black font-black uppercase tracking-widest px-3 py-1.5 text-[9px] hover:bg-[#BEF264] active:scale-95 transition-all">Save weight</button>
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-[8px] uppercase tracking-widest text-zinc-500">Views</div>
              <div className="font-mono text-xs">{a.impressions}</div>
            </div>
            <div className="text-center">
              <div className="text-[8px] uppercase tracking-widest text-zinc-500">Clicks</div>
              <div className="font-mono text-xs text-lime-300">{a.clicks}</div>
            </div>
            <div className="text-center">
              <div className="text-[8px] uppercase tracking-widest text-zinc-500">CTR</div>
              <div className="font-mono text-xs">{a.impressions ? ((a.clicks / a.impressions) * 100).toFixed(1) : "0.0"}%</div>
            </div>
            <button data-testid={`ad-adjust-${a.id}`}
              onClick={() => { if (editing === a.id) { setEditing(null); setEditAdj(null); } else { setEditing(a.id); setEditAdj({ fit: a.fit || "contain", zoom: a.zoom || 100, pos_x: a.pos_x ?? 50, pos_y: a.pos_y ?? 50 }); } }}
              className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${editing === a.id ? "border-pb-cyan text-pb-cyan" : "border-zinc-700 text-zinc-400 hover:border-pb-cyan hover:text-pb-cyan"}`}>
              {editing === a.id ? "Close" : "Adjust"}
            </button>
            <button data-testid={`ad-toggle-${a.id}`} onClick={() => toggle(a)}
              className={`text-[8px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${a.active ? "border-emerald-500/60 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>
              {a.active ? "Live" : "Paused"}
            </button>
            <button data-testid={`ad-delete-${a.id}`} onClick={() => remove(a)} className="p-1.5 text-zinc-500 hover:text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {editing === a.id && editAdj && (
            <div className="mt-3 space-y-2">
              <AdArtworkAdjuster src={a.image} size={a.size} value={editAdj} onChange={setEditAdj} testid={`ad-adjuster-${a.id}`} ad={a} />
              <button data-testid={`ad-adjust-save-${a.id}`} onClick={() => saveAdjust(a)} disabled={busy === `adj-${a.id}`}
                className="flex items-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest px-4 py-2 text-[10px] hover:bg-white active:scale-95 transition-all disabled:opacity-40">
                {busy === `adj-${a.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save Position
              </button>
            </div>
          )}
          </div>
        ))}
      </div>
    </div>
  );
}
